import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import type { components } from '@/api/types'

export type AdminMessage = NonNullable<components['schemas']['AdminMessage']>

export const CONVERSATION_MESSAGES_QUERY_KEY = ['admin', 'conversation-messages'] as const

/**
 * `GET /admin/conversations/{ulid}/messages` (DEC-MOD-007).
 *
 * Egy beszélgetés üzeneteinek admin-nézete — a help-requestből indított
 * üzenet-moderáláshoz. A már soft-deleted üzeneteket is mutatja (deleted_at).
 */
export function useConversationMessages(conversationUlid: string | null) {
  return useQuery({
    queryKey: [...CONVERSATION_MESSAGES_QUERY_KEY, conversationUlid] as const,
    queryFn: async (): Promise<AdminMessage[]> => {
      const { data, error } = await apiClient.GET('/admin/conversations/{id}/messages', {
        params: { path: { id: conversationUlid ?? '' } },
      })
      if (error) throw error
      const envelope = data as { data?: AdminMessage[] } | undefined
      return envelope?.data ?? []
    },
    enabled: !!conversationUlid,
  })
}

interface DeleteMessageVariables {
  // DEC-SECURITY-002 §Tier 5 — opaque message public_id ULID.
  id: string
  reason?: string | null
}

/**
 * `DELETE /admin/messages/{id}` (DEC-ADMIN-009).
 *
 * Soft-delete egy spam üzenetet (`messages.deleted_at`-et állít). A
 * `moderation_actions` row-t ír (action=delete_message), audit-trail.
 * A reason opcionális.
 *
 * Sikerre invalidate a beszélgetés üzenet-listáját (DEC-MOD-007) + a
 * moderation-actions cache-t.
 */
export function useDeleteMessage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, reason }: DeleteMessageVariables): Promise<void> => {
      const body: { reason?: string | null } = {}
      if (reason != null) body.reason = reason
      const { error } = await apiClient.DELETE('/admin/messages/{id}', {
        params: { path: { id } },
        body: Object.keys(body).length > 0 ? body : undefined,
      })
      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CONVERSATION_MESSAGES_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'moderation-actions'] })
    },
  })
}
